/**
 * CompliancePage — Public trust signal page for Nutramilo (iter261c).
 *
 * Fetches live compliance scan result from /api/public/compliance/status
 * and renders a clean, trust-oriented layout with:
 *   - Big headline pass-rate + timestamp
 *   - Three tile row: Security · Nutramilo Core · AIR Blackbox
 *   - Regulatory classification card (EU AI Act limited-risk, MDR outside-scope, GDPR Art. 9)
 *   - Framework crosswalk (EU AI Act / ISO 42001 / NIST / Colorado / GDPR)
 *   - "What this means for you" patient-facing explainer
 *   - Link to /backend compliance docs
 *
 * Design:
 *   - Uses same isDark/language hooks as other public pages
 *   - Emerald primary (trust colour), amber warning, red critical
 *   - No login required — pure trust builder
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import AppFooter from "@/components/AppFooter";
import {
  Shield, CheckCircle2, AlertTriangle, Calendar, Lock,
  ArrowLeft, ExternalLink, FileCheck, Scale, Activity, Clock,
  Download,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompliancePage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [status, setStatus] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/public/compliance/status`, { timeout: 200000 });
        if (!cancelled) setStatus(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Unable to load compliance status");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const fetchAudit = async () => {
      try {
        const { data } = await axios.get(`${API}/public/compliance/audit-stats`, { timeout: 10000 });
        if (!cancelled) setAudit(data);
      } catch {
        // Non-blocking — widget just hides if unavailable
      }
    };
    fetchStatus();
    fetchAudit();
    // Live refresh every 30s so visitors see breathing numbers
    const t = setInterval(fetchAudit, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const cardCls = isDark
    ? "bg-[hsl(223,47%,11%)] border border-white/5"
    : "bg-white border border-gray-100 shadow-sm";

  const passRateColor = (rate) => {
    if (rate >= 80) return "text-emerald-600 dark:text-emerald-400";
    if (rate >= 60) return "text-amber-600 dark:text-amber-400";
    return "text-orange-600 dark:text-orange-400";
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(bg ? "bg-BG" : "en-GB", {
      day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className={isDark ? "min-h-screen bg-[hsl(223,47%,7%)] text-gray-100" : "min-h-screen bg-[#F8FAFC] text-gray-900"}>
      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
        {/* Brand + back */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-block">
            <img src="/app-icon.png" alt="Nutramilo" className="h-9 w-9 object-contain sm:hidden" />
            <img src="/nutramilo-logo.png" alt="Nutramilo" className="hidden sm:block h-12 w-auto object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          </Link>
          <Link
            to="/"
            data-testid="compliance-back-home"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors
              ${isDark ? "bg-white/5 hover:bg-white/10 text-gray-300" : "bg-white border border-gray-200 hover:border-gray-300 text-gray-700"}`}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {bg ? "Начало" : "Home"}
          </Link>
        </div>

        {/* Hero */}
        <header className="mb-10" data-testid="compliance-hero">
          <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider opacity-70">
            <Shield className="w-4 h-4 text-emerald-500" />
            <span>{bg ? "Прозрачност и съответствие" : "Transparency & Compliance"}</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
                {bg ? "Доверие, доказано с данни" : "Trust, shown in numbers"}
              </h1>
              <p className={`text-base sm:text-lg max-w-2xl ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg
                  ? "Nutramilo се сканира автоматично всяка седмица срещу EU AI Act, GDPR, ISO 42001, NIST AI RMF и вътрешни здравни стандарти. По-долу виждате живите резултати от последното сканиране."
                  : "Nutramilo is automatically scanned every week against the EU AI Act, GDPR, ISO 42001, NIST AI RMF, and our internal health-platform standards. Below are the live results of the latest scan."}
              </p>
            </div>
            <a
              href={`${API}/public/compliance/report.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="compliance-download-pdf"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg hover:shadow-xl transition-all flex-shrink-0"
            >
              <Download className="w-4 h-4" />
              {bg ? "Изтегли PDF отчет" : "Download PDF report"}
            </a>
          </div>
        </header>

        {loading && (
          <div className={`${cardCls} rounded-2xl p-8 text-center`} data-testid="compliance-loading">
            <div className="animate-pulse flex flex-col items-center gap-3">
              <Activity className="w-8 h-8 opacity-40" />
              <p className={isDark ? "text-gray-400" : "text-gray-500"}>
                {bg ? "Зареждане на последния отчет…" : "Loading latest report…"}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className={`${cardCls} rounded-2xl p-6 border-2 border-orange-400`} data-testid="compliance-error">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold mb-1">{bg ? "Временно недостъпно" : "Temporarily unavailable"}</div>
                <p className="text-sm opacity-80">{error}</p>
              </div>
            </div>
          </div>
        )}

        {status && (
          <div className="space-y-6" data-testid="compliance-content">
            {/* Headline score */}
            <div className={`${cardCls} rounded-3xl p-6 sm:p-10`} data-testid="compliance-headline">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <div className="text-[11px] uppercase tracking-wider opacity-60 mb-2 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" />
                    {bg ? "Последно сканиране:" : "Last scanned:"} {formatDate(status.timestamp)}
                  </div>
                  <div className="text-lg sm:text-xl font-semibold mb-1">
                    {bg ? "Общо съответствие" : "Overall compliance"}
                  </div>
                  <div className={`text-6xl sm:text-7xl font-bold tracking-tight ${passRateColor(status.pass_rate_percent)}`} data-testid="compliance-pass-rate">
                    {status.pass_rate_percent}%
                  </div>
                  <div className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {status.static_checks.passing} {bg ? "от" : "of"} {status.static_checks.total} {bg ? "проверки преминаха" : "checks passed"}
                  </div>
                </div>
                <div className="flex flex-col gap-2 min-w-[220px]">
                  <StatRow
                    icon={CheckCircle2}
                    label={bg ? "Преминали" : "Passing"}
                    value={status.static_checks.passing}
                    color="text-emerald-600 dark:text-emerald-400"
                  />
                  <StatRow
                    icon={AlertTriangle}
                    label={bg ? "Предупреждения" : "Warnings"}
                    value={status.static_checks.warnings}
                    color="text-amber-600 dark:text-amber-400"
                  />
                  <StatRow
                    icon={AlertTriangle}
                    label={bg ? "Пропадания" : "Failing"}
                    value={status.static_checks.failing}
                    color="text-orange-600 dark:text-orange-400"
                  />
                </div>
              </div>
            </div>

            {/* Three tile row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Tile
                cardCls={cardCls}
                testId="compliance-security-tile"
                icon={Lock}
                iconColor="text-emerald-500"
                title={bg ? "Сигурност" : "Security"}
                main={status.security.high_severity === 0 && status.security.medium_severity_in_production === 0
                  ? (bg ? "Няма" : "None")
                  : `${status.security.high_severity + status.security.medium_severity_in_production}`}
                mainColor={status.security.high_severity === 0 && status.security.medium_severity_in_production === 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-orange-600 dark:text-orange-400"}
                sub={bg
                  ? `${status.security.high_severity} критични · ${status.security.medium_severity_in_production} средни (Bandit)`
                  : `${status.security.high_severity} high · ${status.security.medium_severity_in_production} medium (Bandit)`}
                note={bg ? "Сканиране на production код" : "Production code scan"}
              />
              <Tile
                cardCls={cardCls}
                testId="compliance-nutramilo-tile"
                icon={FileCheck}
                iconColor="text-emerald-500"
                title={bg ? "Основни Nutramilo проверки" : "Nutramilo Core Checks"}
                main={`${status.nutramilo_core_checks.passed}/${status.nutramilo_core_checks.total}`}
                mainColor={status.nutramilo_core_checks.all_pass
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-amber-600 dark:text-amber-400"}
                sub={status.nutramilo_core_checks.all_pass
                  ? (bg ? "Всички преминаха" : "All passing")
                  : (bg ? "Някои неуспешни" : "Some failing")}
                note={bg
                  ? "TTL · GDPR изтриване · cookie consent · разделение пациент/лекар"
                  : "TTL · GDPR erasure · cookie consent · patient/clinician separation"}
              />
              <Tile
                cardCls={cardCls}
                testId="compliance-airblackbox-tile"
                icon={Shield}
                iconColor="text-sky-500"
                title={bg ? "AIR Blackbox EU AI Act" : "AIR Blackbox EU AI Act"}
                main={`${status.static_checks.passing - status.nutramilo_core_checks.passed}/${status.static_checks.total - status.nutramilo_core_checks.total}`}
                mainColor="text-sky-600 dark:text-sky-400"
                sub={bg ? "Статични проверки" : "Static checks"}
                note={bg ? "Articles 9-15 + GDPR" : "Articles 9-15 + GDPR"}
              />
            </div>

            {/* Live 24h audit transparency — reads .air.json directly */}
            {audit && audit.total_calls > 0 && (
              <div className={`${cardCls} rounded-2xl p-6 sm:p-8`} data-testid="compliance-audit-live">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Activity className="w-5 h-5 text-emerald-500" />
                  <h2 className="text-xl font-semibold">
                    {bg ? "Одит в реално време — последни 24 часа" : "Live audit feed — last 24 hours"}
                  </h2>
                  <span className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                    </span>
                    LIVE
                  </span>
                </div>
                <p className={`text-xs mb-5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {bg
                    ? "Прозрачност на всеки AI разговор. Данните се опресняват автоматично на всеки 30 секунди."
                    : "Transparency of every AI conversation. Data refreshes automatically every 30 seconds."}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MonStat
                    label={bg ? "AI заявки (24ч)" : "AI calls (24h)"}
                    value={audit.total_calls}
                    colour="text-sky-600 dark:text-sky-400"
                    testId="audit-total-calls"
                  />
                  <MonStat
                    label={bg ? "Процент успех" : "Success rate"}
                    value={`${audit.success_rate_percent}%`}
                    colour={audit.success_rate_percent >= 95 ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}
                    testId="audit-success-rate"
                  />
                  <MonStat
                    label={bg ? "Средна латентност" : "Avg latency"}
                    value={audit.avg_latency_ms >= 1000 ? `${(audit.avg_latency_ms/1000).toFixed(1)}s` : `${audit.avg_latency_ms}ms`}
                    colour="text-sky-600 dark:text-sky-400"
                    testId="audit-avg-latency"
                  />
                  <MonStat
                    label={bg ? "PII редакции" : "PII redactions"}
                    value={audit.pii_requests_redacted}
                    colour="text-emerald-600 dark:text-emerald-400"
                    testId="audit-pii-redactions"
                    sub={audit.pii_requests_detected > 0
                      ? `${audit.redaction_rate_percent}% ${bg ? "успех" : "effective"}`
                      : (bg ? "няма PII" : "no PII seen")}
                  />
                  <MonStat
                    label={bg ? "Опити за prompt injection" : "Prompt injection attempts"}
                    value={audit.injection_attempts}
                    colour={audit.injection_attempts === 0 ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}
                    testId="audit-injection"
                    sub={bg ? "автоматично флагвани" : "auto-flagged"}
                  />
                  <MonStat
                    label={bg ? "Неуспешни заявки" : "Failed calls"}
                    value={audit.error_calls}
                    colour={audit.error_calls === 0 ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}
                    testId="audit-errors"
                  />
                  <MonStat
                    label={bg ? "Модели в употреба" : "Models in use"}
                    value={Object.keys(audit.model_distribution || {}).length}
                    colour="text-sky-600 dark:text-sky-400"
                    testId="audit-models-count"
                    sub={Object.keys(audit.model_distribution || {}).slice(0, 2).join(" · ") || "—"}
                  />
                  <MonStat
                    label={bg ? "Последна заявка" : "Latest call"}
                    value={audit.latest_call_at
                      ? new Date(audit.latest_call_at).toLocaleTimeString(bg ? "bg-BG" : "en-GB", { hour: "2-digit", minute: "2-digit" })
                      : "—"}
                    colour="text-sky-600 dark:text-sky-400"
                    testId="audit-latest"
                    sub={audit.latest_call_at
                      ? new Date(audit.latest_call_at).toLocaleDateString(bg ? "bg-BG" : "en-GB", { day: "numeric", month: "short" })
                      : ""}
                  />
                </div>
                <div className={`text-xs mt-5 pt-4 border-t ${isDark ? "border-white/5 text-gray-400" : "border-gray-100 text-gray-600"}`}>
                  {bg
                    ? "Никакви промпти, лични данни или идентификатори не се показват — само агрегирани метрики. Всяко AI обаждане има SHA-256 одитен запис за tamper-evident проследяване."
                    : "No prompts, personal data, or identifiers are shown — only aggregated metrics. Every AI call has a SHA-256 audit record for tamper-evident traceability."}
                </div>
              </div>
            )}

            {/* Real-time production AI monitoring */}
            {status.production_monitoring && (
              <div className={`${cardCls} rounded-2xl p-6 sm:p-8`} data-testid="compliance-monitoring">
                <div className="flex items-center gap-2 mb-4">
                  <Activity className="w-5 h-5 text-emerald-500" />
                  <h2 className="text-xl font-semibold">
                    {bg ? "Наблюдение на AI в реално време (7 дни)" : "Real-time AI monitoring (7 days)"}
                  </h2>
                  <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                    {bg ? "LIVE" : "LIVE"}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MonStat
                    label={bg ? "LLM заявки" : "LLM requests"}
                    value={status.production_monitoring.total_calls_7d}
                    colour="text-sky-600 dark:text-sky-400"
                    testId="mon-total-calls"
                  />
                  <MonStat
                    label={bg ? "Процент успех" : "Success rate"}
                    value={`${status.production_monitoring.success_rate_percent}%`}
                    colour="text-emerald-600 dark:text-emerald-400"
                    testId="mon-success-rate"
                  />
                  <MonStat
                    label={bg ? "PII редактирани" : "PII redacted"}
                    value={status.production_monitoring.pii_redacted_7d}
                    colour="text-emerald-600 dark:text-emerald-400"
                    testId="mon-pii-redacted"
                    sub={bg ? "преди LLM" : "before LLM"}
                  />
                  <MonStat
                    label={bg ? "PII достигнали LLM" : "PII leaked to LLM"}
                    value={status.production_monitoring.pii_leaked_to_llm_7d}
                    colour={status.production_monitoring.pii_leaked_to_llm_7d === 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}
                    testId="mon-pii-leaked"
                    sub={bg ? "трябва да е 0" : "target: 0"}
                  />
                </div>
                <div className={`text-xs mt-4 pt-4 border-t ${isDark ? "border-white/5 text-gray-400" : "border-gray-100 text-gray-600"}`}>
                  {bg
                    ? "Всяка AI заявка минава през трейс-слой, който засича PII (имейл, телефон, IBAN, ЕГН) и редактира преди LLM да ги види. Заявките с инжекция на промпт се флагват автоматично."
                    : "Every AI request passes through a trust layer that detects PII (email, phone, IBAN, EGN) and redacts it before the LLM sees it. Prompt-injection attempts are flagged automatically."}
                </div>
              </div>
            )}

            {/* Regulatory classification */}
            <div className={`${cardCls} rounded-2xl p-6 sm:p-8`} data-testid="compliance-regulatory">
              <div className="flex items-center gap-2 mb-4">
                <Scale className="w-5 h-5 text-sky-500" />
                <h2 className="text-xl font-semibold">
                  {bg ? "Регулаторна позиция" : "Regulatory classification"}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <RegBlock
                  cardCls={isDark ? "bg-white/5" : "bg-emerald-50/60"}
                  title={bg ? "Регламент на ЕС за изкуствен интелект" : "EU AI Act"}
                  value={bg ? "Limited-risk (Член 52)" : "Limited-risk (Article 52)"}
                  desc={bg
                    ? "Прозрачност към потребителя + AI disclosure на всяка AI функция."
                    : "User-facing transparency + AI disclosure on every AI feature."}
                />
                <RegBlock
                  cardCls={isDark ? "bg-white/5" : "bg-emerald-50/60"}
                  title={bg ? "EU MDR 2017/745" : "EU MDR 2017/745"}
                  value={bg ? "Извън обхвата" : "Outside scope"}
                  desc={bg
                    ? "Образователен wellness инструмент — не поставя диагнози, не класифицира, не предписва."
                    : "Educational wellness tool — does not diagnose, classify, or prescribe."}
                />
                <RegBlock
                  cardCls={isDark ? "bg-white/5" : "bg-emerald-50/60"}
                  title="GDPR"
                  value={bg ? "Член 9 — изрично съгласие" : "Article 9 — explicit consent"}
                  desc={bg
                    ? "Двойно съгласие за всякакви медицински образи + право на изтриване."
                    : "Double consent for any medical image + right-to-erasure."}
                />
              </div>
            </div>

            {/* Framework crosswalk */}
            <div className={`${cardCls} rounded-2xl p-6 sm:p-8`} data-testid="compliance-frameworks">
              <h2 className="text-xl font-semibold mb-4">
                {bg ? "Покрити рамки" : "Frameworks covered"}
              </h2>
              <div className="flex flex-wrap gap-2">
                {status.frameworks_covered.map((fw) => (
                  <span
                    key={fw}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium
                      ${isDark ? "bg-white/5 text-gray-200 ring-1 ring-white/10" : "bg-sky-50 text-sky-700 ring-1 ring-sky-200"}`}
                    data-testid={`compliance-framework-${fw.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {fw}
                  </span>
                ))}
              </div>
            </div>

            {/* Patient-facing explainer */}
            <div className={`${cardCls} rounded-2xl p-6 sm:p-8`} data-testid="compliance-explainer">
              <h2 className="text-xl font-semibold mb-3">
                {bg ? "Какво означава това за теб" : "What this means for you"}
              </h2>
              <ul className={`space-y-3 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>{bg ? "Прозрачност." : "Transparency."}</strong>{" "}
                    {bg
                      ? "Всяка AI функция е ясно означена като образователен инструмент, не медицинско изделие."
                      : "Every AI feature is clearly labelled as an educational tool, not a medical device."}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>{bg ? "Твоите данни са твои." : "Your data is yours."}</strong>{" "}
                    {bg
                      ? "Можеш да поискаш изтриване по всяко време (GDPR Член 17). Анонимните данни се триат автоматично след 30 дни."
                      : "You can request deletion at any time (GDPR Article 17). Anonymous data is auto-deleted after 30 days."}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>{bg ? "Нулев compromise на сигурността." : "Zero security compromises."}</strong>{" "}
                    {bg
                      ? "Последният Bandit scan на production код отчете 0 критични и 0 средни уязвимости."
                      : "The latest Bandit scan of production code reported 0 high and 0 medium vulnerabilities."}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>{bg ? "Независими одити." : "Independent audits."}</strong>{" "}
                    {bg
                      ? "Използваме отворения AIR Blackbox scanner (Apache-2.0) — не сме си поставили сами оценките."
                      : "We use the open-source AIR Blackbox scanner (Apache-2.0) — we don't grade our own homework."}
                  </span>
                </li>
              </ul>
            </div>

            {/* Cadence footer */}
            <div className={`${cardCls} rounded-2xl p-5 flex items-start gap-3`} data-testid="compliance-cadence">
              <Clock className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
              <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                <strong>{bg ? "Честота:" : "Cadence:"}</strong>{" "}
                {bg
                  ? "Пълен scan всеки понеделник в 05:00 UTC. Custom Nutramilo проверки се изпълняват при всеки PR през GitHub Actions."
                  : "Full scan every Monday at 05:00 UTC. Custom Nutramilo checks run on every PR via GitHub Actions."}{" "}
                <a
                  href="https://github.com/airblackbox/gateway"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-sky-600 dark:text-sky-400 hover:underline"
                  data-testid="compliance-airblackbox-link"
                >
                  AIR Blackbox <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
      <AppFooter />
    </div>
  );
}

function StatRow({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="flex items-center gap-2 opacity-70">
        <Icon className="w-4 h-4" /> {label}
      </span>
      <span className={`font-bold text-lg ${color}`}>{value}</span>
    </div>
  );
}

function Tile({ cardCls, testId, icon: Icon, iconColor, title, main, mainColor, sub, note }) {
  return (
    <div className={`${cardCls} rounded-2xl p-5`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <span className="text-[11px] uppercase tracking-wider opacity-70">{title}</span>
      </div>
      <div className={`text-3xl font-bold mb-1 ${mainColor}`}>{main}</div>
      <div className="text-sm opacity-80 mb-2">{sub}</div>
      <div className="text-[11px] opacity-50">{note}</div>
    </div>
  );
}

function RegBlock({ cardCls, title, value, desc }) {
  return (
    <div className={`${cardCls} rounded-xl p-4`}>
      <div className="text-[11px] uppercase tracking-wider opacity-60 mb-1">{title}</div>
      <div className="text-base font-semibold mb-2">{value}</div>
      <div className="text-xs opacity-70">{desc}</div>
    </div>
  );
}

function MonStat({ label, value, colour, testId, sub }) {
  return (
    <div className="text-center" data-testid={testId}>
      <div className={`text-3xl font-bold mb-1 ${colour}`}>{value}</div>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      {sub && <div className="text-[10px] opacity-50 mt-0.5">{sub}</div>}
    </div>
  );
}
